export interface SearchFiltersState {
  containsText: string
  tags: string[]
  ownerUser: string[]
  modifiedTime: string | null
}

export const MODIFIED_TIME_PRESETS = [
  'now-1h',
  'now-24h',
  'now-7d',
  'now-14d',
  'now-30d',
] as const

export type ModifiedTimePreset = (typeof MODIFIED_TIME_PRESETS)[number]

const MODIFIED_PRESET_SET = new Set<string>(MODIFIED_TIME_PRESETS)

export function emptySearchFiltersState(): SearchFiltersState {
  return {
    containsText: '',
    tags: [],
    ownerUser: [],
    modifiedTime: null,
  }
}

export function quoteQueryValue(value: string): string {
  if (!value) {
    return '""'
  }
  if (/[\s:]/.test(value) || value.includes('"')) {
    return `"${value.replace(/"/g, '\\"')}"`
  }
  return value
}

export function formatTagQualifier(tagKey: string): string {
  return `Tag:${quoteQueryValue(tagKey)}`
}

function formatOwnerUserQualifier(value: string): string {
  return `OwnerUser:${quoteQueryValue(value)}`
}

function formatModifiedTimeQualifier(value: string): string {
  if (MODIFIED_PRESET_SET.has(value)) {
    return `ModifiedTime:>=${value}`
  }
  // Absolute calendar date, range `[start TO end]`, or other baked values
  return `ModifiedTime:${value}`
}

export function isAbsoluteModifiedDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
}

/** Stored form: `[YYYY-MM-DD TO YYYY-MM-DD]` */
export function isAbsoluteModifiedDateRange(value: string): boolean {
  return /^\[\d{4}-\d{2}-\d{2} TO \d{4}-\d{2}-\d{2}\]$/.test(value)
}

export function formatModifiedDateRange(start: string, end: string): string {
  if (start === end) {
    return start
  }
  return `[${start} TO ${end}]`
}

export function parseModifiedDateRange(value: string): [string, string] | null {
  const range = value.match(/^\[(\d{4}-\d{2}-\d{2}) TO (\d{4}-\d{2}-\d{2})\]$/)
  if (range) {
    return [range[1], range[2]]
  }
  if (isAbsoluteModifiedDate(value)) {
    return [value, value]
  }
  return null
}

/**
 * Tokenize a search query into atoms. Respects double-quoted strings.
 * Does not implement a full boolean AST — enough for Tag:/ModifiedTime: split.
 */
export function tokenizeQuery(query: string): string[] {
  const trimmed = query.trim()
  if (!trimmed) {
    return []
  }

  const raw: string[] = []
  const re =
    /([^\s:]+:"[^"]*")|([^\s:]+:[[{][^\]}]*[\]}])|("[^"]*")|([()])|(\S+)/g
  let match: RegExpExecArray | null
  while ((match = re.exec(trimmed)) !== null) {
    raw.push(match[0])
  }

  // Split parentheses glued to qualifiers: "(Tag:a" / "Tag:b)"
  const tokens: string[] = []
  for (const token of raw) {
    if (token === '(' || token === ')' || token.startsWith('"')) {
      tokens.push(token)
      continue
    }
    let rest = token
    while (rest.startsWith('(')) {
      tokens.push('(')
      rest = rest.slice(1)
    }
    const trailing: string[] = []
    while (rest.endsWith(')')) {
      trailing.unshift(')')
      rest = rest.slice(0, -1)
    }
    if (rest) {
      tokens.push(rest)
    }
    tokens.push(...trailing)
  }
  return tokens
}

function isOrOperator(token: string): boolean {
  return token.toUpperCase() === 'OR'
}

function isAndOperator(token: string): boolean {
  return token.toUpperCase() === 'AND'
}

function isBooleanOperator(token: string): boolean {
  const upper = token.toUpperCase()
  return upper === 'AND' || upper === 'OR' || upper === 'NOT'
}

function isTagToken(token: string): boolean {
  return /^Tag:/i.test(token)
}

function isModifiedTimeToken(token: string): boolean {
  return /^ModifiedTime:/i.test(token)
}

function isOwnerUserToken(token: string): boolean {
  return /^OwnerUser:/i.test(token)
}

/** Strip the `prefix:` and any surrounding quotes from a qualifier token. */
function unquoteQualifierValue(token: string): string {
  const colon = token.indexOf(':')
  let value = token.slice(colon + 1)
  if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
    value = value.slice(1, -1).replace(/\\"/g, '"')
  }
  return value
}

const getTagKey = unquoteQualifierValue
const getOwnerUserValue = unquoteQualifierValue

function parseModifiedValue(token: string): string | null {
  const colon = token.indexOf(':')
  let value = token.slice(colon + 1)
  // Accept ModifiedTime:>=… and ModifiedTime:…
  if (value.startsWith('>=')) {
    value = value.slice(2)
  }
  if (MODIFIED_PRESET_SET.has(value)) {
    return value
  }
  if (isAbsoluteModifiedDate(value) || isAbsoluteModifiedDateRange(value)) {
    return value
  }
  return null
}

/** Build `prefix:a OR prefix:b`; wrap in ( ) only when combined with other query parts. */
function formatOrClause(
  values: string[],
  formatQualifier: (value: string) => string,
  options: { wrap?: boolean } = {},
): string | null {
  const qualifiers = values.filter(Boolean).map(formatQualifier)
  if (qualifiers.length === 0) {
    return null
  }
  if (qualifiers.length === 1) {
    return qualifiers[0]
  }
  const joined = qualifiers.join(' OR ')
  return options.wrap ? `(${joined})` : joined
}

/** Build Tag:… OR Tag:… ; wrap in ( ) only when combined with other query parts. */
export function formatTagsClause(
  tags: string[],
  options: { wrap?: boolean } = {},
): string | null {
  return formatOrClause(tags, formatTagQualifier, options)
}

/** Build OwnerUser:… OR OwnerUser:… ; wrap in ( ) only when combined with other query parts. */
function formatOwnerUsersClause(
  owners: string[],
  options: { wrap?: boolean } = {},
): string | null {
  return formatOrClause(owners, formatOwnerUserQualifier, options)
}

export function buildSearchQuery(state: SearchFiltersState): string {
  const parts: string[] = []
  const text = (state.containsText ?? '').trim()
  // Parentheses only needed when free text (or other filters) sit beside the OR group
  const wrapTags = !!text || state.ownerUser.length > 0 || !!state.modifiedTime
  const wrapOwners = !!text || state.tags.length > 0 || !!state.modifiedTime

  if (text) {
    parts.push(text)
  }

  const tagsClause = formatTagsClause(state.tags, { wrap: wrapTags })
  if (tagsClause) {
    parts.push(tagsClause)
  }

  const ownersClause = formatOwnerUsersClause(state.ownerUser, {
    wrap: wrapOwners,
  })
  if (ownersClause) {
    parts.push(ownersClause)
  }

  if (state.modifiedTime) {
    parts.push(formatModifiedTimeQualifier(state.modifiedTime))
  }

  return parts.join(' ')
}

/**
 * Extract a contiguous `prefix:a OR prefix:b` group (optionally parenthesized)
 * starting at `start`. Returns values + end index (exclusive), or null if not
 * an OR group of the given qualifier.
 */
function tryExtractOrGroup(
  tokens: string[],
  start: number,
  isToken: (token: string) => boolean,
  getValue: (token: string) => string,
): { values: string[]; end: number } | null {
  let i = start
  let wrapped = false

  if (tokens[i] === '(') {
    wrapped = true
    i += 1
  }

  if (!tokens[i] || !isToken(tokens[i])) {
    return null
  }

  const values: string[] = []
  const firstValue = getValue(tokens[i])
  if (!firstValue) {
    return null
  }
  values.push(firstValue)
  i += 1

  // Need at least one OR qualifier: to treat as OR group (multi-value filter)
  let sawOr = false
  while (i < tokens.length && isOrOperator(tokens[i])) {
    const next = tokens[i + 1]
    if (!next || !isToken(next)) {
      return null
    }
    const value = getValue(next)
    if (!value) {
      return null
    }
    sawOr = true
    values.push(value)
    i += 2
  }

  if (wrapped) {
    if (tokens[i] !== ')') {
      return null
    }
    i += 1
    // Parenthesized single qualifier:(qualifier:a) is unusual; allow multi OR only
    if (!sawOr && values.length === 1) {
      // Still accept single value in parens as one filter value
      return { values, end: i }
    }
  } else if (!sawOr) {
    // Bare single qualifier:a — extract as one value (not an OR group, but still a filter value)
    return { values, end: i }
  }

  return { values, end: i }
}

interface OrGroupExtractor {
  isToken: (token: string) => boolean
  getValue: (token: string) => string
  values: string[]
}

const TAG_EXTRACTOR: Omit<OrGroupExtractor, 'values'> = {
  isToken: isTagToken,
  getValue: getTagKey,
}

const OWNER_USER_EXTRACTOR: Omit<OrGroupExtractor, 'values'> = {
  isToken: isOwnerUserToken,
  getValue: getOwnerUserValue,
}

/** True only for a token that is both the right prefix AND carries a real value. */
function isValidQualifierToken(
  token: string | undefined,
  extractor: OrGroupExtractor,
): boolean {
  return !!token && extractor.isToken(token) && !!extractor.getValue(token)
}

function mergeValues(target: string[], values: string[]) {
  for (const value of values) {
    if (!target.includes(value)) {
      target.push(value)
    }
  }
}

/** Consume `AND prefix:a OR prefix:b` at `start` (right after the AND token). */
function tryConsumeAndGroup(
  tokens: string[],
  start: number,
  extractor: OrGroupExtractor,
): number | null {
  const group = tryExtractOrGroup(
    tokens,
    start,
    extractor.isToken,
    extractor.getValue,
  )
  if (
    !group ||
    !(
      group.values.length > 1 ||
      extractor.isToken(tokens[start]) ||
      tokens[start] === '('
    )
  ) {
    return null
  }
  mergeValues(extractor.values, group.values)
  return group.end
}

/** Consume `prefix:a` or `prefix:a OR prefix:b` at `start`. */
function tryConsumeOrGroup(
  tokens: string[],
  start: number,
  extractor: OrGroupExtractor,
): number | null {
  const group = tryExtractOrGroup(
    tokens,
    start,
    extractor.isToken,
    extractor.getValue,
  )
  if (!group) {
    return null
  }
  // Implicit AND (prefix:a prefix:b without OR) must stay in free text — filter is OR-only.
  if (group.values.length === 1) {
    const prev = tokens[start - 1]
    const next = tokens[group.end]
    if (
      isValidQualifierToken(prev, extractor) ||
      isValidQualifierToken(next, extractor)
    ) {
      return null
    }
  }
  mergeValues(extractor.values, group.values)
  return group.end
}

function cleanupFreeText(parts: string[]): string {
  // Drop dangling AND/OR and empty parens left after extracting filter clauses
  const cleaned: string[] = []
  for (const part of parts) {
    if (!part) {
      continue
    }
    cleaned.push(part)
  }

  // Remove operators at edges and duplicate operators
  while (cleaned.length && isBooleanOperator(cleaned[0])) {
    cleaned.shift()
  }
  while (cleaned.length && isBooleanOperator(cleaned[cleaned.length - 1])) {
    cleaned.pop()
  }

  // Remove empty () pairs
  const withoutEmptyParens: string[] = []
  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i] === '(' && cleaned[i + 1] === ')') {
      i += 1
      continue
    }
    withoutEmptyParens.push(cleaned[i])
  }

  return withoutEmptyParens.join(' ').trim()
}

export function parseSearchQuery(query: string): SearchFiltersState {
  const state = emptySearchFiltersState()
  const trimmed = query.trim()
  if (!trimmed) {
    return state
  }

  const tokens = tokenizeQuery(trimmed)
  const freeParts: string[] = []
  const tags: string[] = []
  const owners: string[] = []
  let modifiedTime: string | null = null
  let i = 0

  const extractors: OrGroupExtractor[] = [
    { ...TAG_EXTRACTOR, values: tags },
    { ...OWNER_USER_EXTRACTOR, values: owners },
  ]

  while (i < tokens.length) {
    const token = tokens[i]

    // Optional leading AND before an OR group (foo AND (Tag:a OR Tag:b))
    if (isAndOperator(token)) {
      const end = extractors
        .map((extractor) => tryConsumeAndGroup(tokens, i + 1, extractor))
        .find((result) => result !== null)
      if (end != null) {
        i = end
        continue
      }
      freeParts.push(token)
      i += 1
      continue
    }

    const end = extractors
      .map((extractor) => tryConsumeOrGroup(tokens, i, extractor))
      .find((result) => result !== null)
    if (end != null) {
      i = end
      continue
    }

    if (isModifiedTimeToken(token)) {
      const parsed = parseModifiedValue(token)
      if (parsed && !modifiedTime) {
        modifiedTime = parsed
      } else {
        freeParts.push(token)
      }
      i += 1
      continue
    }

    freeParts.push(token)
    i += 1
  }

  state.tags = tags
  state.ownerUser = owners
  state.modifiedTime = modifiedTime
  state.containsText = cleanupFreeText(freeParts)
  return state
}
